/**
 * Chat Store
 *
 * Zustand store for chat state management:
 * - Conversations list
 * - Messages by conversation
 * - Online users
 * - Typing indicators (actor-based)
 * - Connection status
 * - Loading states
 *
 * Uses HTTP-only cookies for authentication
 */

import { create } from 'zustand';
import { chatApi } from '@/src/shared/api/chat.api';
import { chatSocketService } from '@/src/shared/services/chat-socket.service';
import type { Conversation, Message, ConnectionStatus } from '../types';

/**
 * Check if user is being redirected to login (auth expiration)
 * Used to suppress error logs during automatic logout
 */
const isRedirectingToLogin = (): boolean => {
  return typeof window !== 'undefined' && window.location.pathname === '/login';
};

interface ChatState {
  // Data
  conversations: Conversation[];
  messagesByConversation: Record<string, Message[]>;
  onlineUsers: Set<string>;
  typingActors: Record<string, Set<string>>; // conversationId -> Set<actorId>

  // UI State
  isLoadingConversations: boolean;
  isLoadingMessages: Record<string, boolean>;
  connectionStatus: ConnectionStatus;
  error: string | null;

  // Pagination state
  paginationByConversation: Record<
    string,
    { hasMore: boolean; offset: number; totalMessages: number }
  >;

  // Actions - Conversations
  loadConversations: () => Promise<void>;
  refreshConversations: () => Promise<void>;
  getOrCreateConversation: (workerActorId: string) => Promise<string>;

  // Actions - Messages
  loadMessages: (
    conversationId: string,
    pagination?: { limit?: number; offset?: number }
  ) => Promise<void>;
  loadMoreMessages: (conversationId: string) => Promise<void>;
  sendMessage: (
    conversationId: string,
    body: string,
    attachmentIds?: string[]
  ) => Promise<void>;
  markAsRead: (conversationId: string, lastReadMessageId: string) => Promise<void>;
  editMessage: (messageId: string, content: string) => Promise<void>;

  // Actions - Real-time
  addMessage: (message: Message) => void;
  updateMessage: (message: Message) => void;
  updateConversationUnreadCount: (conversationId: string, unreadCount: number) => void;
  setTyping: (conversationId: string, actorId: string) => void;
  removeTyping: (conversationId: string, actorId: string) => void;
  setUserOnline: (userId: string) => void;
  setUserOffline: (userId: string) => void;

  // Actions - WebSocket
  connectSocket: () => void;
  disconnectSocket: () => void;
  joinConversation: (conversationId: string) => Promise<void>;

  // Helpers
  getMessages: (conversationId: string) => Message[];
  getTypingActors: (conversationId: string) => string[];
  isUserOnline: (userId: string) => boolean;

  // Reset state (useful when switching actors)
  reset: () => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  // Initial state
  conversations: [],
  messagesByConversation: {},
  onlineUsers: new Set(),
  typingActors: {},
  isLoadingConversations: false,
  isLoadingMessages: {},
  connectionStatus: 'connecting',
  error: null,
  paginationByConversation: {},

  // Load conversations
  loadConversations: async () => {
    set({ isLoadingConversations: true, error: null });

    try {
      const conversations = await chatApi.getConversations();
      set({ conversations, isLoadingConversations: false });
    } catch (error) {
      // Don't log error if redirecting to login (auth expiration handled automatically)
      if (isRedirectingToLogin()) {
        return;
      }

      console.error('[ChatStore] Failed to load conversations:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load conversations',
        isLoadingConversations: false,
      });
    }
  },

  // Refresh conversations
  refreshConversations: async () => {
    try {
      const conversations = await chatApi.getConversations();
      set({ conversations });
    } catch (error) {
      // Silently fail if redirecting to login
      if (isRedirectingToLogin()) {
        return;
      }
      console.error('[ChatStore] Failed to refresh conversations:', error);
    }
  },

  // Get or create conversation with a worker
  getOrCreateConversation: async (workerActorId: string) => {
    try {
      const response = await chatApi.getOrCreateConversation(workerActorId);
      // Refresh conversations to include the new/existing conversation
      await get().refreshConversations();
      return response.id;
    } catch (error) {
      // Silently fail if redirecting to login
      if (isRedirectingToLogin()) {
        throw error;
      }
      console.error('[ChatStore] Failed to get or create conversation:', error);
      throw error;
    }
  },

  // Load messages
  loadMessages: async (conversationId, pagination = {}) => {
    set((state) => ({
      isLoadingMessages: { ...state.isLoadingMessages, [conversationId]: true },
      error: null,
    }));

    try {
      const response = await chatApi.getConversation(conversationId, pagination);

      set((state) => ({
        messagesByConversation: {
          ...state.messagesByConversation,
          [conversationId]: response.messages,
        },
        paginationByConversation: {
          ...state.paginationByConversation,
          [conversationId]: {
            hasMore: response.pagination.hasMore,
            offset: response.pagination.offset + response.messages.length,
            totalMessages: response.totalMessages,
          },
        },
        isLoadingMessages: { ...state.isLoadingMessages, [conversationId]: false },
      }));
    } catch (error) {
      if (isRedirectingToLogin()) {
        return;
      }
      console.error('[ChatStore] Failed to load messages:', error);
      set((state) => ({
        error: error instanceof Error ? error.message : 'Failed to load messages',
        isLoadingMessages: { ...state.isLoadingMessages, [conversationId]: false },
      }));
    }
  },

  // Load more messages (pagination)
  loadMoreMessages: async (conversationId) => {
    const pagination = get().paginationByConversation[conversationId];
    if (!pagination?.hasMore) return;

    const currentMessages = get().messagesByConversation[conversationId] || [];

    try {
      const response = await chatApi.getConversation(conversationId, {
        limit: 50,
        offset: pagination.offset,
      });

      if (response.messages.length > 0) {
        set((state) => ({
          messagesByConversation: {
            ...state.messagesByConversation,
            [conversationId]: [...response.messages, ...currentMessages],
          },
          paginationByConversation: {
            ...state.paginationByConversation,
            [conversationId]: {
              hasMore: response.pagination.hasMore,
              offset: pagination.offset + response.messages.length,
              totalMessages: response.totalMessages,
            },
          },
        }));
      }
    } catch (error) {
      if (isRedirectingToLogin()) {
        return;
      }
      console.error('[ChatStore] Failed to load more messages:', error);
    }
  },

  // Send message
  sendMessage: async (conversationId, body, attachmentIds) => {
    if (!chatSocketService.isConnected()) {
      // Fallback to HTTP
      try {
        const message = await chatApi.sendMessage(
          conversationId,
          body,
          attachmentIds
        );
        get().addMessage(message);
      } catch (error) {
        if (isRedirectingToLogin()) {
          throw error;
        }
        console.error('[ChatStore] Failed to send message via HTTP:', error);
        throw error;
      }
      return;
    }

    // Send via WebSocket
    try {
      await chatSocketService.sendMessage(
        conversationId,
        body,
        'TEXT',
        undefined,
        attachmentIds
      );
      // Message will be added via message_sent event
    } catch (error) {
      if (isRedirectingToLogin()) {
        throw error;
      }
      console.error('[ChatStore] Failed to send message via WebSocket:', error);
      throw error;
    }
  },

  // Mark as read
  markAsRead: async (conversationId, lastReadMessageId) => {
    try {
      // Send via WebSocket if connected
      if (chatSocketService.isConnected()) {
        chatSocketService.markAsRead(conversationId, lastReadMessageId);
      } else {
        // Fallback to HTTP
        await chatApi.markAsRead(conversationId, lastReadMessageId);
      }

      // Update unread count in conversations list
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        ),
      }));
    } catch (error) {
      if (isRedirectingToLogin()) {
        return;
      }
      console.error('[ChatStore] Failed to mark messages as read:', error);
    }
  },

  // Edit message
  editMessage: async (messageId, content) => {
    try {
      if (chatSocketService.isConnected()) {
        await chatSocketService.editMessage(messageId, content);
      }
      // Response will come via message_edited event
    } catch (error) {
      if (isRedirectingToLogin()) {
        throw error;
      }
      console.error('[ChatStore] Failed to edit message:', error);
      throw error;
    }
  },

  // Add message (real-time)
  addMessage: (message) => {
    set((state) => {
      const messages = state.messagesByConversation[message.conversationId] || [];

      // Check if message already exists
      const exists = messages.some((msg) => msg.id === message.id);
      if (exists) return state;

      return {
        messagesByConversation: {
          ...state.messagesByConversation,
          [message.conversationId]: [...messages, message],
        },
      };
    });

    // Update last message in conversations list
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === message.conversationId
          ? {
              ...conv,
              lastMessage: {
                id: message.id,
                body: message.body,
                createdAt: message.createdAt,
                senderActorId: message.senderActorId,
              },
              lastMessageAt: message.createdAt,
            }
          : conv
      ),
    }));
  },

  // Update message (real-time)
  updateMessage: (message) => {
    set((state) => ({
      messagesByConversation: {
        ...state.messagesByConversation,
        [message.conversationId]: state.messagesByConversation[
          message.conversationId
        ]?.map((msg) => (msg.id === message.id ? message : msg)),
      },
    }));
  },

  // Update conversation unread count (real-time)
  updateConversationUnreadCount: (conversationId, unreadCount) => {
    console.log('[ChatStore] Updating conversation unread count:', { conversationId, unreadCount });
    set((state) => {
      const updatedConversations = state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, unreadCount } : conv
      );
      console.log('[ChatStore] Conversations after update:', updatedConversations.map(c => ({ id: c.id, unreadCount: c.unreadCount })));
      return { conversations: updatedConversations };
    });
  },

  // Set typing
  setTyping: (conversationId, actorId) => {
    set((state) => {
      const typingActorsForConversation =
        new Set(state.typingActors[conversationId]) || new Set();
      typingActorsForConversation.add(actorId);

      return {
        typingActors: {
          ...state.typingActors,
          [conversationId]: typingActorsForConversation,
        },
      };
    });
  },

  // Remove typing
  removeTyping: (conversationId, actorId) => {
    set((state) => {
      const typingActorsForConversation =
        new Set(state.typingActors[conversationId]) || new Set();
      typingActorsForConversation.delete(actorId);

      return {
        typingActors: {
          ...state.typingActors,
          [conversationId]: typingActorsForConversation,
        },
      };
    });
  },

  // Set user online
  setUserOnline: (userId) => {
    set((state) => {
      const onlineUsers = new Set(state.onlineUsers);
      onlineUsers.add(userId);
      return { onlineUsers };
    });
  },

  // Set user offline
  setUserOffline: (userId) => {
    set((state) => {
      const onlineUsers = new Set(state.onlineUsers);
      onlineUsers.delete(userId);
      return { onlineUsers };
    });
  },

  // Connect socket (fetches token from HTTP-only cookie via endpoint)
  connectSocket: () => {
    // Connect is now async
    chatSocketService.connect().catch((error) => {
      console.error('[ChatStore] Failed to connect socket:', error);
      set({ error: error.message || 'Failed to connect to chat server' });
    });

    // Set up event handlers
    chatSocketService.on('connection_status', (status) => {
      set({ connectionStatus: status });
    });

    chatSocketService.on('connect', () => {
      // Connected
    });

    chatSocketService.on('disconnect', () => {
      // Disconnected
    });

    chatSocketService.on('new_message', (data) => {
      if (data?.message) {
        get().addMessage(data.message);
      }
    });

    chatSocketService.on('message_sent', (data) => {
      if (data?.message) {
        get().addMessage(data.message);
      }
    });

    chatSocketService.on('message_edited', ({ message }) => {
      get().updateMessage(message);
    });

    chatSocketService.on('actor_typing', ({ conversationId, actorId }) => {
      get().setTyping(conversationId, actorId);
    });

    chatSocketService.on('actor_stopped_typing', ({ conversationId, actorId }) => {
      get().removeTyping(conversationId, actorId);
    });

    chatSocketService.on('user_online', ({ userId }) => {
      get().setUserOnline(userId);
    });

    chatSocketService.on('user_offline', ({ userId }) => {
      get().setUserOffline(userId);
    });

    chatSocketService.on('messages_read', ({ conversationId }) => {
      // Update unread count when messages are read
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        ),
      }));
    });

    chatSocketService.on('conversation_unread_updated', ({ conversationId, unreadCount }) => {
      // Update unread count from server (for both increment and clear)
      console.log('[ChatStore] Unread count updated:', { conversationId, unreadCount });
      get().updateConversationUnreadCount(conversationId, unreadCount);
    });

    chatSocketService.on('offline_messages', ({ messages }) => {
      // Add offline messages
      messages.forEach((message) => {
        get().addMessage(message);
      });
    });

    chatSocketService.on('error', ({ message, code }) => {
      console.error('[ChatStore] Socket error:', message, code);
      set({ error: message });
    });
  },

  // Disconnect socket
  disconnectSocket: () => {
    chatSocketService.disconnect();
    set({ connectionStatus: 'disconnected' });
  },

  // Join conversation
  joinConversation: async (conversationId) => {
    try {
      await chatSocketService.joinConversation(conversationId);
    } catch (error) {
      if (isRedirectingToLogin()) {
        throw error;
      }
      console.error('[ChatStore] Failed to join conversation:', error);
      throw error;
    }
  },

  // Helpers
  getMessages: (conversationId) => {
    return get().messagesByConversation[conversationId] || [];
  },

  getTypingActors: (conversationId) => {
    const typingActorsSet = get().typingActors[conversationId];
    return typingActorsSet ? Array.from(typingActorsSet) : [];
  },

  isUserOnline: (userId) => {
    return get().onlineUsers.has(userId);
  },

  // Reset all state (useful when switching actors)
  reset: () => {
    console.log('[ChatStore] Resetting chat state');
    set({
      conversations: [],
      messagesByConversation: {},
      onlineUsers: new Set(),
      typingActors: {},
      isLoadingConversations: false,
      isLoadingMessages: {},
      error: null,
      paginationByConversation: {},
    });
  },
}));
